// config/conversationApiEndpoints.js
// Import centralized API configuration
import {
  BASE_API,
  API_VERSION,
  API_BASE_URL,
  WEBSOCKET_URL,
  WEBSOCKET_CHAT_URL
} from './apiConfig';
import { asyncStorage } from '../services/storage/asyncStorageService';
import logger from '../services/logging/logger';

// Re-export for backward compatibility
export { BASE_API, API_VERSION, API_BASE_URL, WEBSOCKET_URL, WEBSOCKET_CHAT_URL };

// Backward compatibility functions (for files that haven't been updated yet)
export const getUserIdFromStorage = () => {
  try {
    const userData = JSON.parse(asyncStorage.get('userData'));
    return userData?.userId || null;
  } catch (error) {
    return null;
  }
};

export const getOrganizationIdFromStorage = () => {
  try {
    const userData = JSON.parse(asyncStorage.get('userData'));
    return userData?.organizationId || null;
  } catch (error) {
    return null;
  }
};

// ✅ NEW: Get bucket name from storage
export const getBucketNameFromStorage = () => {
  try {
    const userData = JSON.parse(asyncStorage.get('userData'));
    return userData?.conversationsBucket || null;
  } catch (error) {
    return null;
  }
};

// Alias for backward compatibility
export const getConnectionId = getUserIdFromStorage;

// ✅ UPDATED: Helper function to create headers with auth token, tenant ID, AND bucket name
export const createApiHeaders = async (tenantId, bucketName = null) => {
  try {
    // Import tokenService for proper token management
    const { tokenService } = await import('../services/auth/tokenService');
    const headers = {
      'Content-Type': 'application/json'
    };
    
    // Get the proper auth token (ID token preferred, access token fallback)
    const authToken = await tokenService.getAuthorizationToken();
    if (authToken) {
      headers['X-Auth-Token'] = `Bearer ${authToken}`;
    }
    
    if (tenantId) {
      // Match Postman exactly - X-Tenant-Id (capital letters)
      headers['X-Tenant-Id'] = tenantId;
    }

    // ✅ ADD BUCKET NAME - Try parameter first, then storage
    const finalBucketName = bucketName || getBucketNameFromStorage();
    if (finalBucketName) {
      headers['X-Bucket-Name'] = finalBucketName;
    } else {
      logger.warn('X-Bucket-Name not found in parameters or storage');
    }

    return headers;
    
  } catch (error) {
    logger.error('Error creating API headers', { error: error.message });
    return {
      'Content-Type': 'application/json'
    };
  }
};

// ✅ UPDATED: Helper function to create headers for file upload (multipart/form-data)
export const createFileUploadHeaders = async (tenantId, bucketName, userId) => {
  try {
    const { tokenService } = await import('../services/auth/tokenService');
    const headers = {};
    
    // Get the proper auth token
    const authToken = await tokenService.getAuthorizationToken();
    if (authToken) {
      headers['X-Auth-Token'] = `Bearer ${authToken}`;
    }
    
    if (tenantId) {
      headers['X-Tenant-Id'] = tenantId;
    }
    
    if (bucketName) {
      headers['X-Bucket-Name'] = bucketName;
    }
    
    if (userId) {
      headers['X-User-Id'] = userId;
    }
    
    logger.debug('Created file upload headers', {
      hasAuthToken: !!headers['X-Auth-Token'],
      hasTenantId: !!headers['X-Tenant-Id'],
      hasBucketName: !!headers['X-Bucket-Name'],
      hasUserId: !!headers['X-User-Id']
    });
    // Note: Don't set Content-Type for FormData - browser will set it automatically with boundary
    
    return headers;
    
  } catch (error) {
    logger.error('Error creating file upload headers', { error: error.message });
    return {};
  }
};

// Chat endpoints
export const CHAT_ENDPOINTS = {
  getAllConversations: (userId) => `${API_BASE_URL}/conversations/${userId}`,
  deleteConversation: (userId, conversationId) => `${API_BASE_URL}/conversations/${userId}/${conversationId}`,
  getConversationChats: (userId, conversationId) => `${API_BASE_URL}/conversations/${userId}/${conversationId}/chats`,
  getSpecificConversationChat: (userId, conversationId, chatId) => `${API_BASE_URL}/conversations/${userId}/${conversationId}/chats/${chatId}`,
  uploadFile: (conversationId) => `${API_BASE_URL}/files/upload?conversationId=${conversationId}`,
  listFiles: (prefix) => `${API_BASE_URL}/files?prefix=${encodeURIComponent(prefix)}`,
  downloadFile: (key) => `${API_BASE_URL}/files/download?key=${encodeURIComponent(key)}`
};